import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localizedField as i18nLocalized } from '../utils/i18nFields';
import { 
  UtensilsCrossed, 
  ChefHat, 
  ShoppingBag, 
  Plus, 
  Minus,
  Search,
  MapPin,
  Globe,
  Coffee,
  BellRing,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

import ChefPickBadge from '../components/ChefPickBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TableMenu = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tableNumber = parseInt(searchParams.get('table')) || null;
  const { user } = useAuth();
  const { addItem, items: cartItems, totalPrice } = useCart();
  const { language, setLanguage, languages } = useLanguage();
  
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cateringMenu, setCateringMenu] = useState(null);
  const [beverageItems, setBeverageItems] = useState([]);
  const [beverageCategories, setBeverageCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alakart');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [callingWaiter, setCallingWaiter] = useState(false);
  const [waiterCalled, setWaiterCalled] = useState(false);

  const handleCallWaiter = async () => {
    if (callingWaiter) return;
    setCallingWaiter(true);
    try {
      const res = await axios.post(`${API}/waiter/call`, {
        table_number: tableNumber,
        reason: 'help',
        language: language
      });
      if (res.data.duplicate) {
        toast.info(txt.waiterCalledAlready);
      } else {
        toast.success(txt.waiterCalled);
      }
      setWaiterCalled(true);
      // Confirmation state lasts 4s, then the button is callable again.
      // (Backend rate-limits same-table calls within 60s and returns duplicate=true.)
      setTimeout(() => setWaiterCalled(false), 4000);
    } catch (err) {
      toast.error('Çağrı gönderilemedi / Call failed');
    } finally {
      setCallingWaiter(false);
    }
  };

  // Translations
  const texts = {
    tr: {
      table: 'Masa',
      welcome: 'Hoş Geldiniz',
      guest: 'Misafir',
      alakart: 'A La Carte',
      dailyMenu: 'Günlük Menü',
      searchPlaceholder: 'Yemek ara...',
      all: 'Tümü',
      addToCart: 'Sepete Ekle',
      add: 'Ekle',
      addedToCart: 'sepete eklendi',
      items: 'ürün',
      orderNow: 'Sipariş Ver',
      tableNotFound: 'Masa Bulunamadı',
      scanQR: 'Lütfen geçerli bir QR kodu tarayın.',
      goHome: 'Ana Sayfaya Git',
      loading: 'Menü yükleniyor...',
      dailyMenuTitle: 'Günlük Menü',
      dailyMenuDesc: 'Günlük menü için catering sayfasını ziyaret edin.',
      viewDaily: 'Günlük Menüyü Gör',
      loginRequired: 'Sipariş vermek için giriş yapmalısınız',
      outOfStock: 'Tükendi',
      noResults: 'Aradığınız kriterlere uygun yemek bulunamadı.',
      callWaiter: 'Garsonu Çağır',
      callWaiterDesc: 'Yardıma mı ihtiyacınız var?',
      waiterCalled: 'Garson çağrıldı, yakında gelecek',
      waiterCalledAlready: 'Çağrınız zaten alındı, lütfen bekleyin',
      bonAppetit: 'Afiyet olsun!',
      heroTagline: 'Lezzetli bir mola için doğru yerdesiniz'
    },
    en: {
      table: 'Table',
      welcome: 'Welcome',
      guest: 'Guest',
      alakart: 'A La Carte',
      dailyMenu: 'Daily Menu',
      searchPlaceholder: 'Search dishes...',
      all: 'All',
      addToCart: 'Add to Cart',
      add: 'Add',
      addedToCart: 'added to cart',
      items: 'items',
      orderNow: 'Order Now',
      tableNotFound: 'Table Not Found',
      scanQR: 'Please scan a valid QR code.',
      goHome: 'Go to Home',
      loading: 'Loading menu...',
      dailyMenuTitle: 'Daily Menu',
      dailyMenuDesc: 'Visit the catering page for daily menu.',
      viewDaily: 'View Daily Menu',
      loginRequired: 'Please login to place an order',
      outOfStock: 'Out of Stock',
      noResults: 'No dishes found matching your criteria.',
      callWaiter: 'Call Waiter',
      callWaiterDesc: 'Need any help?',
      waiterCalled: 'Waiter is on the way',
      waiterCalledAlready: 'Your call is already received, please wait',
      bonAppetit: 'Enjoy your meal!',
      heroTagline: 'Right place for a delicious break'
    },
    me: {
      table: 'Sto',
      welcome: 'Dobrodošli',
      guest: 'Gost',
      alakart: 'A La Carte',
      dailyMenu: 'Dnevni Meni',
      searchPlaceholder: 'Pretraži jela...',
      all: 'Sve',
      addToCart: 'Dodaj u Korpu',
      add: 'Dodaj',
      addedToCart: 'dodato u korpu',
      items: 'artikala',
      orderNow: 'Naruči Sada',
      tableNotFound: 'Sto Nije Pronađen',
      scanQR: 'Molimo skenirajte važeći QR kod.',
      goHome: 'Idi na Početnu',
      loading: 'Učitavanje menija...',
      dailyMenuTitle: 'Dnevni Meni',
      dailyMenuDesc: 'Posjetite stranicu keteringa za dnevni meni.',
      viewDaily: 'Pogledaj Dnevni Meni',
      loginRequired: 'Molimo prijavite se za narudžbu',
      outOfStock: 'Nema',
      noResults: 'Nema jela koja odgovaraju vašim kriterijumima.',
      callWaiter: 'Pozovi Konobara',
      callWaiterDesc: 'Treba vam pomoć?',
      waiterCalled: 'Konobar dolazi uskoro',
      waiterCalledAlready: 'Vaš poziv je već primljen',
      bonAppetit: 'Prijatno!',
      heroTagline: 'Pravo mjesto za ukusnu pauzu'
    },
    ru: {
      table: 'Стол',
      welcome: 'Добро пожаловать',
      guest: 'Гость',
      alakart: 'А Ла Карт',
      dailyMenu: 'Дневное Меню',
      searchPlaceholder: 'Поиск блюд...',
      all: 'Все',
      addToCart: 'В корзину',
      add: 'Добавить',
      addedToCart: 'добавлено в корзину',
      items: 'товаров',
      orderNow: 'Заказать',
      tableNotFound: 'Стол Не Найден',
      scanQR: 'Пожалуйста, отсканируйте действительный QR код.',
      goHome: 'На Главную',
      loading: 'Загрузка меню...',
      dailyMenuTitle: 'Дневное Меню',
      dailyMenuDesc: 'Посетите страницу кейтеринга для дневного меню.',
      viewDaily: 'Смотреть Дневное Меню',
      loginRequired: 'Пожалуйста, войдите для заказа',
      outOfStock: 'Нет',
      noResults: 'Блюда по вашим критериям не найдены.',
      callWaiter: 'Позвать Официанта',
      callWaiterDesc: 'Нужна помощь?',
      waiterCalled: 'Официант скоро подойдёт',
      waiterCalledAlready: 'Ваш вызов уже принят',
      bonAppetit: 'Приятного аппетита!',
      heroTagline: 'Идеальное место для вкусной паузы'
    },
    sr: {
      table: 'Сто',
      welcome: 'Добродошли',
      guest: 'Гост',
      alakart: 'А Ла Карт',
      dailyMenu: 'Дневни Мени',
      searchPlaceholder: 'Претражи јела...',
      all: 'Све',
      addToCart: 'Додај у Корпу',
      add: 'Додај',
      addedToCart: 'додато у корпу',
      items: 'артикала',
      orderNow: 'Наручи Сада',
      tableNotFound: 'Сто Није Пронађен',
      scanQR: 'Молимо скенирајте важећи QR код.',
      goHome: 'Иди на Почетну',
      loading: 'Учитавање менија...',
      dailyMenuTitle: 'Дневни Мени',
      dailyMenuDesc: 'Посетите страницу кетеринга за дневни мени.',
      viewDaily: 'Погледај Дневни Мени',
      loginRequired: 'Молимо пријавите се за наручивање',
      outOfStock: 'Нема',
      noResults: 'Нема јела по вашим критеријумима.',
      callWaiter: 'Позови Конобара',
      callWaiterDesc: 'Треба вам помоћ?',
      waiterCalled: 'Конобар стиже ускоро',
      waiterCalledAlready: 'Ваш позив је већ примљен',
      bonAppetit: 'Пријатно!',
      heroTagline: 'Право место за укусну паузу'
    }
  };

  const txt = texts[language] || texts.en;

  // Category translations - same as Alakart
  const categoryTranslations = {
    tr: { 
      all: 'Tümü', 
      'Kahvaltı': 'Kahvaltı',
      'Tostlar': 'Tostlar',
      'Menemenler': 'Menemenler',
      'Omletler': 'Omletler',
      'Burgerler': 'Burgerler',
      'Soğuk Sandwichler': 'Soğuk Sandviçler',
      'Sıcak Sandwichler': 'Sıcak Sandviçler',
      'Ana Yemekler': 'Ana Yemekler',
      'Makarnalar': 'Makarnalar',
      'Salatalar': 'Salatalar',
      'Ekstralar': 'Ekstralar',
    },
    en: { 
      all: 'All', 
      'Kahvaltı': 'Breakfast',
      'Tostlar': 'Toasts',
      'Menemenler': 'Menemen',
      'Omletler': 'Omelettes',
      'Burgerler': 'Burgers',
      'Soğuk Sandwichler': 'Cold Sandwiches',
      'Sıcak Sandwichler': 'Hot Sandwiches',
      'Ana Yemekler': 'Main Dishes',
      'Makarnalar': 'Pasta',
      'Salatalar': 'Salads',
      'Ekstralar': 'Extras',
    },
    me: { 
      all: 'Sve', 
      'Kahvaltı': 'Doručak',
      'Tostlar': 'Tost Sendviči',
      'Menemenler': 'Menemeni',
      'Omletler': 'Omleti',
      'Burgerler': 'Burgeri',
      'Soğuk Sandwichler': 'Hladni Sendviči',
      'Sıcak Sandwichler': 'Topli Sendviči',
      'Ana Yemekler': 'Glavna Jela',
      'Makarnalar': 'Tjestenina',
      'Salatalar': 'Salate',
      'Ekstralar': 'Dodaci',
    },
    ru: { 
      all: 'Все', 
      'Kahvaltı': 'Завтрак',
      'Tostlar': 'Тосты',
      'Menemenler': 'Менемен',
      'Omletler': 'Омлеты',
      'Burgerler': 'Бургеры',
      'Soğuk Sandwichler': 'Холодные сэндвичи',
      'Sıcak Sandwichler': 'Горячие сэндвичи',
      'Ana Yemekler': 'Основные блюда',
      'Makarnalar': 'Паста',
      'Salatalar': 'Салаты',
      'Ekstralar': 'Дополнения',
    },
    sr: { 
      all: 'Све', 
      'Kahvaltı': 'Доручак',
      'Tostlar': 'Тостови',
      'Menemenler': 'Менемени',
      'Omletler': 'Омлети',
      'Burgerler': 'Бургери',
      'Soğuk Sandwichler': 'Хладни сендвичи',
      'Sıcak Sandwichler': 'Топли сендвичи',
      'Ana Yemekler': 'Главна јела',
      'Makarnalar': 'Тестенина',
      'Salatalar': 'Салате',
      'Ekstralar': 'Додаци',
    }
  };

  const catTrans = categoryTranslations[language] || categoryTranslations.en;

  // Localized name/description (tr/en/me/ru/sr with fallback)
  const getItemName = (item) => i18nLocalized(item, 'name', language);
  const getItemDescription = (item) => i18nLocalized(item, 'description', language);

  const fetchMenu = useCallback(async () => {
    try {
      const [menuRes, catRes] = await Promise.all([
        axios.get(`${API}/menu`),
        axios.get(`${API}/menu/categories`)
      ]);
      setMenuItems(menuRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Menu fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCatering = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/catering/today`);
      setCateringMenu(response.data);
    } catch (error) {
      console.error('Catering fetch error:', error);
    }
  }, []);

  const fetchBeverages = useCallback(async () => {
    try {
      const [itemsRes, catRes] = await Promise.all([
        axios.get(`${API}/beverages`),
        axios.get(`${API}/beverages/categories`)
      ]);
      setBeverageItems(itemsRes.data);
      setBeverageCategories(catRes.data);
    } catch (error) {
      console.error('Beverages fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchMenu();
    fetchCatering();
    fetchBeverages();
    if (tableNumber) {
      sessionStorage.setItem('tableNumber', String(tableNumber));
    }
  }, [tableNumber, fetchMenu, fetchCatering, fetchBeverages]);

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const itemName = getItemName(item).toLowerCase();
    const itemDesc = getItemDescription(item).toLowerCase();
    const matchesSearch = itemName.includes(searchTerm.toLowerCase()) ||
                         itemDesc.includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const goToCheckout = () => {
    // Misafir olarak da sipariş verilebilir
    navigate(`/checkout?table=${tableNumber}&guest=true`);
  };

  if (!tableNumber) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md">
          <MapPin className="w-16 h-16 text-terracotta mx-auto mb-4" />
          <h1 className="font-playfair text-2xl font-bold text-charcoal mb-2">{txt.tableNotFound}</h1>
          <p className="text-charcoal/70 mb-4">{txt.scanQR}</p>
          <Button onClick={() => navigate('/')} className="bg-basil text-white hover:bg-basil/90">
            {txt.goHome}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-basil border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-charcoal/70">{txt.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pb-48 font-outfit">
      {/* Header — Welcoming hero with table number, restaurant name, and waiter call */}
      <div className="relative bg-gradient-to-br from-charcoal via-charcoal to-stone-800 text-white pt-7 pb-9 sticky top-0 z-40 overflow-hidden">
        {/* Decorative accents */}
        <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-terracotta/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-basil/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-11 h-11 bg-gradient-to-br from-terracotta to-orange-700 rounded-2xl flex items-center justify-center shadow-lg shadow-terracotta/40 ring-1 ring-white/10">
                    <span className="font-bold text-lg">{tableNumber}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white/50 text-[10px] uppercase tracking-[0.3em] leading-none">{txt.table}</span>
                    <span className="text-white/90 text-sm font-medium mt-0.5">{txt.welcome}{user?.name ? `, ${user.name}` : ''} ✨</span>
                  </div>
                </div>
                <h1 className="font-playfair text-2xl md:text-4xl font-bold tracking-tight leading-tight">
                  Central <span className="text-terracotta italic font-medium">Restaurant</span>
                </h1>
                <p className="text-white/60 text-xs md:text-sm mt-1 max-w-md hidden sm:block">{txt.heroTagline}</p>
              </motion.div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1.5 hover:bg-white/10 text-white rounded-full border border-white/15 px-3" data-testid="table-lang-btn">
                    <Globe className="w-4 h-4" />
                    <span className="text-sm font-medium">{languages.find(l => l.code === language)?.flag}</span>
                    <span className="text-xs uppercase tracking-wider opacity-70">{language}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`flex items-center gap-2 cursor-pointer ${language === lang.code ? 'bg-basil/10' : ''}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Call Waiter button — primary CTA on hero */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleCallWaiter}
                disabled={callingWaiter || waiterCalled}
                data-testid="call-waiter-btn"
                className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg transition-all border ${
                  waiterCalled
                    ? 'bg-basil/20 text-basil border-basil/40 cursor-default'
                    : 'bg-white text-charcoal border-white hover:bg-amber-50 hover:border-amber-200 shadow-amber-500/20'
                }`}
              >
                <BellRing className={`w-4 h-4 ${callingWaiter ? 'animate-bounce' : ''}`} />
                <span>{waiterCalled ? '✓ ' + txt.waiterCalled.split(',')[0] : txt.callWaiter}</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Selector */}
      <div className="bg-white border-b border-stone-200 sticky top-[88px] z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 py-3">
            <button
              onClick={() => setActiveTab('alakart')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                activeTab === 'alakart' ? 'bg-charcoal text-white shadow-md shadow-charcoal/20' : 'bg-stone-100 text-charcoal/70 hover:bg-stone-200'
              }`}
            >
              <UtensilsCrossed className="w-4 h-4" />
              {txt.alakart}
            </button>
            <button
              onClick={() => setActiveTab('catering')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                activeTab === 'catering' ? 'bg-charcoal text-white shadow-md shadow-charcoal/20' : 'bg-stone-100 text-charcoal/70 hover:bg-stone-200'
              }`}
            >
              <ChefHat className="w-4 h-4" />
              {txt.dailyMenu}
            </button>
            <button
              onClick={() => setActiveTab('beverages')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                activeTab === 'beverages' ? 'bg-charcoal text-white shadow-md shadow-charcoal/20' : 'bg-stone-100 text-charcoal/70 hover:bg-stone-200'
              }`}
            >
              <Coffee className="w-4 h-4" />
              {language === 'tr' ? 'Icecekler' : language === 'me' ? 'Pica' : 'Beverages'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'alakart' && (
          <>
            {/* Search & Filter - Same as Alakart */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                <Input
                  type="text"
                  placeholder={txt.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 bg-white border-stone-200 rounded-xl h-12"
                  data-testid="table-menu-search-input"
                />
              </div>

              {/* Category Filter */}
              <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory('all')}
                  className={`rounded-full whitespace-nowrap ${
                    selectedCategory === 'all' 
                      ? 'bg-basil text-white hover:bg-basil/90' 
                      : 'bg-white hover:bg-stone-50'
                  }`}
                  data-testid="table-category-all-btn"
                >
                  {catTrans.all || 'All'}
                </Button>
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory(cat)}
                    className={`rounded-full whitespace-nowrap ${
                      selectedCategory === cat 
                        ? 'bg-basil text-white hover:bg-basil/90' 
                        : 'bg-white hover:bg-stone-50'
                    }`}
                    data-testid={`table-category-${cat.toLowerCase().replace(' ', '-')}-btn`}
                  >
                    {catTrans[cat] || cat}
                  </Button>
                ))}
              </div>
            </div>

            {/* Menu Grid - Same card design as Alakart */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                    layout
                  >
                    <MenuCard item={item} txt={txt} getItemName={getItemName} getItemDescription={getItemDescription} catTrans={catTrans} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-16">
                <p className="text-charcoal/60 text-lg">
                  {txt.noResults}
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'catering' && (
          <div className="text-center py-12">
            <ChefHat className="w-16 h-16 text-basil mx-auto mb-4" />
            <h2 className="font-playfair text-2xl font-bold text-charcoal mb-2">{txt.dailyMenuTitle}</h2>
            <p className="text-charcoal/70 mb-4">{txt.dailyMenuDesc}</p>
            <Button onClick={() => navigate(`/catering?table=${tableNumber}`)} className="bg-terracotta text-white hover:bg-terracotta/90">
              {txt.viewDaily}
            </Button>
          </div>
        )}

        {activeTab === 'beverages' && (
          <>
            {beverageItems.length === 0 ? (
              <div className="text-center py-12">
                <Coffee className="w-16 h-16 text-charcoal/20 mx-auto mb-4" />
                <p className="text-charcoal/60">{language === 'tr' ? 'Icecek menusu bos' : 'No beverages available'}</p>
              </div>
            ) : (
              <>
                <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
                  <Button
                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('all')}
                    className={`rounded-full whitespace-nowrap ${selectedCategory === 'all' ? 'bg-basil text-white' : 'bg-white'}`}
                  >
                    {catTrans.all || 'All'}
                  </Button>
                  {beverageCategories.map(cat => (
                    <Button key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} onClick={() => setSelectedCategory(cat)}
                      className={`rounded-full whitespace-nowrap ${selectedCategory === cat ? 'bg-basil text-white' : 'bg-white'}`}>
                      {cat}
                    </Button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence mode="popLayout">
                    {beverageItems.filter(b => (selectedCategory === 'all' || b.category === selectedCategory) && (!searchTerm || b.name.toLowerCase().includes(searchTerm.toLowerCase()))).map((item, index) => (
                      <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: index * 0.05 }} layout>
                        <BeverageTableCard item={item} txt={txt} getItemName={getItemName} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Fixed Cart Bar — Animated, premium */}
      <AnimatePresence>
        {cartItems.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-14 left-3 right-3 md:left-4 md:right-4 z-[9999]"
            data-testid="table-cart-bar"
          >
            <button
              onClick={goToCheckout}
              data-testid="table-checkout-btn"
              className="w-full bg-gradient-to-r from-terracotta via-orange-600 to-terracotta text-white rounded-2xl shadow-2xl shadow-terracotta/40 px-4 py-3.5 flex items-center justify-between gap-3 hover:shadow-terracotta/60 hover:scale-[1.01] active:scale-[0.99] transition-all group ring-1 ring-white/20"
            >
              <div className="flex items-center gap-3">
                <div className="relative w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center ring-1 ring-white/20">
                  <ShoppingBag className="w-5 h-5 text-white" />
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-terracotta text-[10px] font-bold rounded-full flex items-center justify-center shadow-md">
                    {cartItems.length}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-white/80 text-[11px] uppercase tracking-wider leading-none">{cartItems.length} {txt.items}</p>
                  <p className="font-playfair text-xl font-bold leading-tight">€{totalPrice.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 font-semibold text-sm">
                <span>{txt.orderNow}</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// MenuCard component - Same design as Alakart
const MenuCard = ({ item, txt, getItemName, getItemDescription, catTrans }) => {
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const { language } = useLanguage();

  const itemName = getItemName(item);
  const itemDesc = getItemDescription(item);

  const handleAdd = () => {
    addItem({
      item_id: item.id,
      name: itemName,
      name_tr: item.name,
      price: item.price,
      quantity: quantity,
      item_type: 'alakart'
    });
    toast.success(`${quantity}x ${itemName} ${txt.addedToCart}`);
    setQuantity(1);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-stone-100/50">
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        {item.image_url ? (
          <img
            src={item.image_url.startsWith('/api') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url}
            alt={itemName}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <UtensilsCrossed className="w-12 h-12 text-stone-300" />
        </div>
        {!item.is_available && (
          <div className="absolute inset-0 bg-charcoal/60 flex items-center justify-center">
            <span className="text-white font-medium">{txt.outOfStock}</span>
          </div>
        )}
        {item.is_featured && (
          <div className="absolute top-3 left-3">
            <ChefPickBadge language={language} />
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-playfair text-xl font-semibold text-charcoal">
            {itemName}
          </h3>
          <span className="text-terracotta font-bold text-lg">
            €{item.price}
          </span>
        </div>
        <p className="text-charcoal/60 text-sm mb-4 line-clamp-2">
          {itemDesc}
        </p>
        <span className="inline-block px-3 py-1 bg-sand rounded-full text-xs font-medium text-charcoal/70 mb-4">
          {catTrans[item.category] || item.category}
        </span>
        
        {item.is_available && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
              <button
                onClick={() => setQuantity(Math.max(0.5, quantity - 0.5))}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                data-testid={`table-quantity-minus-${item.id}`}
              >
                <Minus className="w-4 h-4 text-charcoal" />
              </button>
              <span className="w-8 text-center font-medium text-charcoal">
                {quantity % 1 === 0 ? quantity : quantity.toFixed(1)}
              </span>
              <button
                onClick={() => setQuantity(quantity + 0.5)}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                data-testid={`table-quantity-plus-${item.id}`}
              >
                <Plus className="w-4 h-4 text-charcoal" />
              </button>
            </div>
            <Button
              onClick={handleAdd}
              className="flex-1 bg-terracotta text-white hover:bg-terracotta/90 rounded-full"
              data-testid={`table-add-to-cart-${item.id}`}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {txt.add}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableMenu;

const BeverageTableCard = ({ item, txt, getItemName }) => {
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const { language } = useLanguage();
  const itemName = getItemName(item);

  const handleAdd = () => {
    addItem({ item_id: item.id, name: itemName, name_tr: item.name, price: item.price, quantity, item_type: 'beverage' });
    toast.success(`${quantity}x ${itemName} ${txt.addedToCart}`);
    setQuantity(1);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-stone-100/50">
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        {item.image_url ? (
          <img src={item.image_url.startsWith('/api') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url} alt={itemName} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Coffee className="w-12 h-12 text-stone-300" /></div>
        {item.is_featured && (
          <div className="absolute top-3 left-3"><ChefPickBadge language={language} /></div>
        )}
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-playfair text-xl font-semibold text-charcoal">{itemName}</h3>
          <span className="text-terracotta font-bold text-lg">€{item.price}</span>
        </div>
        <span className="inline-block px-3 py-1 bg-sand rounded-full text-xs font-medium text-charcoal/70 mb-4">{item.category}</span>
        {item.is_available && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-medium">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
            </div>
            <Button onClick={handleAdd} className="flex-1 bg-terracotta text-white hover:bg-terracotta/90 rounded-full">
              <ShoppingBag className="w-4 h-4 mr-2" />{txt.add}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
